import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { LearnosityRequest } from '../../models/LearnosityRequest';
import { useAuth0 } from '@auth0/auth0-react';
import { isTokenExpired } from '../../utils/isTokenExpired';
import refreshTokenOrLogout from '../../utils/refreshTokenOrLogout';
import { ProgressBar } from 'primereact/progressbar';
import { Button } from 'primereact/button';
import { ArrowLeft, ArrowRight, RefreshCw } from 'lucide-react';
import {
  useLearnosityItemsInitialization,
  type LearnosityInitializationRefetch,
} from '../../custom-hooks/useLearnosityItemsInitialization';
import { useNavigate } from 'react-router-dom';
import EphemeralMessage from '../StudentSingleSkill/EphemeralMessage';
import { usePostHog } from '@posthog/react';
import { ErrorBoundaryUI } from '../ErrorBoundary/ErrorBoundaryUI';

const TOTAL_QUESTION = 11;

interface AssessmentWrapperProps {
  request: LearnosityRequest;
  refetchInitialization: LearnosityInitializationRefetch;
  setLoading: (state: boolean) => void;
  loading: boolean;
  setTooltipKey: Dispatch<SetStateAction<number>>;
  selectedGradeLevel: { name: string; activityId: string } | null;
  testQuestion: number;
  setTestQuestion: Dispatch<SetStateAction<number>>;
  showArrowMessage: boolean;
  onArrowMessageShown: () => void;
}

const DiagnosticAssessmentDemoWrapper: FC<AssessmentWrapperProps> = ({
  request,
  refetchInitialization,
  setLoading,
  selectedGradeLevel,
  testQuestion,
  loading,
  setTooltipKey,
  setTestQuestion,
  showArrowMessage,
  onArrowMessageShown,
}) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, logout } = useAuth0();
  const [restartKey, setRestartKey] = useState(0);
  const [previewCompleted, setPreviewCompleted] = useState<boolean>(false);
  const [ephemeralArrowMessage] = useState<boolean>(showArrowMessage);
  const posthog = usePostHog();

  const checkTokenRefresh = async () => {
    if (isTokenExpired()) {
      await refreshTokenOrLogout(getAccessTokenSilently, logout, posthog);
    }
  };

  const goToNext = async () => {
    if (itemsAppRef.current) {
      itemsAppRef.current
        .items()
        .next()
        .then(() => {
          setTestQuestion(prev => prev + 1);
        })
        .catch(() => {
          itemsAppRef.current?.submit({ show_submit_confirmation: false, show_submit_ui: false });
        });
    }
  };

  const goToBack = async () => {
    if (itemsAppRef.current) {
      if (previewCompleted) {
        setPreviewCompleted(false);
        return;
      }
      itemsAppRef.current.items().goto(testQuestion - 1);
      setTestQuestion(prev => prev - 1);
    }
  };

  const goToFirst = () => {
    if (!itemsAppRef.current) return;
    setLoading(true);
    setTestQuestion(0);
    setPreviewCompleted(false);
    setRestartKey(key => key + 1);
  };

  const { status, itemsAppRef } = useLearnosityItemsInitialization({
    reloadOn10001AfterReady: true,
    initObj: request,
    restartKey,
    assessmentType: 'diagnostic_demo',
    refetchInitialization,
    getContainer: () => document.getElementById('learnosity_assess'),
    onReady: itemsApp => {
      setLoading(false);
      itemsApp.on('test:start', () => {
        void checkTokenRefresh();
      });
    },
  });
  useEffect(() => {
    if (status !== 'idle') setLoading(status === 'initializing' || status === 'retry_wait');
  }, [status, setLoading]);

  useEffect(() => {
    if (showArrowMessage) {
      onArrowMessageShown();
    }
  }, []);

  useEffect(() => {
    const wrapButton = () => {
      const btn = document.getElementById('lrn_assess_next_btn');
      const interval = setInterval(() => {
        const buttonLabel = document.querySelector('#lrn_assess_next_btn .btn-label');
        if (buttonLabel) {
          buttonLabel.textContent = 'Next Question';
          clearInterval(interval);
        }
      }, 250);

      if (btn && !btn.parentElement?.classList.contains('nc-tooltip-wrapper')) {
        const wrapper = document.createElement('div');
        wrapper.className = 'nc-tooltip-wrapper';
        wrapper.style.display = 'inline-block';
        btn.parentNode?.insertBefore(wrapper, btn);
        wrapper.appendChild(btn);
      }
      return () => clearInterval(interval);
    };
    wrapButton();
    const observer = new MutationObserver(wrapButton);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
    setTooltipKey(prev => prev + 1);
    return () => {
      observer.disconnect();
    };
  }, [loading]);

  return (
    <>
      <div className="tw-flex tw-flex-row tw-w-full tw-gap-x-4 tw-items-center">
        <Button
          outlined
          icon={<ArrowLeft size={20} />}
          className={`${testQuestion === 0 ? 'tw-invisible' : ''} nc_diagnostic-preview-button`}
          onClick={goToBack}
        />

        <div className="tw-w-full nc_diagnostic-content-teacher-preview tw-bg-white tw-px-10 tw-py-6 tw-min-h-[660px]">
          <div className="tw-flex tw-justify-between tw-pb-4">
            <div className="tw-flex tw-items-center">
              <span className="tw-font-atkinson tw-italic tw-text-neutral-700 tw-text-lg">
                {selectedGradeLevel?.name} diagnostic
              </span>
            </div>
            <Button label="Save my progress" className="!tw-border-none !tw-bg-white " link disabled />
          </div>
          <div
            className="tw-w-full tw-flex tw-items-center tw-p-[8px] tw-border tw-border-[2px] tw-border-solid tw-rounded-full tw-p-2 tw-mb-4 tw-border-neutral-200"
            id="diagnostic-progress-bar"
          >
            <ProgressBar
              style={{
                height: '1rem',
                width: '100%',
              }}
              pt={{ value: { className: 'tw-bg-[#992FE5]', 'data-tooltip': `${testQuestion} / ${TOTAL_QUESTION}` } }}
              showValue={false}
              value={testQuestion === 0 ? 1 : (testQuestion / TOTAL_QUESTION) * 100}
              mode="determinate"
              className="inline-block custom-progress-bar tw-rounded-2xl"
            ></ProgressBar>
          </div>
          {previewCompleted && (
            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-py-10 tw-gap-y-20">
              <div className="tw-flex tw-flex-col tw-gap-y-4 tw-items-center">
                <img src={'preview_completed.png'} alt="preview_completed" className="tw-h-auto tw-object-contain" />
                <h1 className="tw-font-sora tw-font-bold tw-text-neutral-950 tw-text-3xl">Preview completed!</h1>
                <p className="tw-text-base tw-font-atkinson tw-text-neutral-950 tw-text-center">
                  Now you have a sense of the types of questions your students will see <br></br>
                  <span className="tw-font-bold">You&apos;re ready to run this with your class</span>
                </p>
              </div>
              <div className="tw-flex tw-flex-row tw-gap-x-4">
                <Button
                  label="Go through it again"
                  link
                  className="tw-gap-x-2 tw-self-start tw-flex tw-flex-row-reverse"
                  icon={<RefreshCw />}
                  onClick={goToFirst}
                />
                <Button label="Back to prep page" outlined onClick={() => navigate('/prep-diagnostics')} />
              </div>
            </div>
          )}
          {status === 'failed' && <ErrorBoundaryUI embedded />}
          <div id="nc_diagnostic-demo" className={previewCompleted ? 'tw-hidden' : ''} hidden={status === 'failed'}>
            <div id="learnosity_assess" className="!tw-relative tw-z-[2] !tw-m-0"></div>
          </div>
        </div>
        <Button
          outlined
          icon={<ArrowRight size={20} />}
          className={` ${previewCompleted ? 'tw-invisible' : ''} nc_diagnostic-preview-button`}
          onClick={() => (testQuestion < TOTAL_QUESTION ? goToNext() : setPreviewCompleted(true))}
          disabled={loading}
        />
      </div>
      {ephemeralArrowMessage && (
        <EphemeralMessage
          delay={1}
          text={
            <>
              Use these arrows to browse all the questions.{' '}
              <span className="tw-font-bold">Students won&apos;t see them on their end</span>
            </>
          }
          ease={[0.34, 1, 1, 1]}
          duration={5}
          customStyle={{
            top: '22rem',
            right: '10px',
            left: 'unset',
            maxWidth: '283px',
            zIndex: '2',
          }}
          strokePositionType="bottom"
        />
      )}
    </>
  );
};

export default DiagnosticAssessmentDemoWrapper;
